import React from "react";
import { FiChevronRight, FiClipboard } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

import ChecklistIcon from './ChecklistIcon';
import { useComplianceCentre } from "../context/ComplianceCentreContext";

export default function ComplianceAreasOverview() {
  const navigate = useNavigate();
  const { data, loading, error, selectedDate } = useComplianceCentre();
  const allChecklists = data?.checklists || [];

  const handleChecklistClick = (slug) => {
    if (!slug) return;

    navigate(`/compliance-center/checks/${slug}?date=${selectedDate}`);
  };

  return (
    <div id="compliance-areas" className="w-full bg-white rounded-2xl p-6 border border-gray-100 shadow-sm font-sans select-none">

      {/* Main Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="text-left space-y-1">
          <h2 className="text-slate-900 font-black text-lg tracking-tight">
            Compliance Areas Overview
          </h2>

          <p className="text-xs font-medium text-slate-400">
            Select a checklist to view and manage its compliance requirements.
          </p>
        </div>

        <span className="text-xs font-bold text-slate-400">
          {allChecklists.length} Checklists
        </span>
      </div>

      {/* Checklist List */}
      <div className="w-full">

        {loading ? (
          <div className="py-10 text-center">
            <p className="text-sm font-bold text-slate-400">
              Loading checklists...
            </p>
          </div>
        ) : allChecklists.length > 0 ? (

          <div className="divide-y divide-gray-100">

            {allChecklists.map((checklist) => (
              <div
                key={checklist.id}
                role="link"
                tabIndex={0}
                onKeyDown={event => { if (event.key === 'Enter') handleChecklistClick(checklist.slug); }}
                title={data.areas.find(area => area.id === checklist.id)?.status?.replaceAll('_', ' ')}
                onClick={() => handleChecklistClick(checklist.slug)}
                className="group flex items-center justify-between py-4 px-3 -mx-3 rounded-xl hover:bg-slate-50 transition-all duration-200 cursor-pointer"
              >

                {/* Checklist Information */}
                <div className="flex items-center gap-3 min-w-0">

                  {/* Dynamic Checklist Icon */}
                  <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-100 transition">
                    <ChecklistIcon
                      checklist={checklist}
                      className="w-5 h-5"
                    />
                  </div>

                  {/* Name */}
                  <div className="min-w-0">
                    <h4 className="text-sm font-extrabold text-slate-900 truncate">
                      {checklist.name}
                    </h4>

                    {checklist.slug && (
                      <p className="text-[10px] font-semibold text-slate-400 truncate mt-0.5">
                        {checklist.slug}
                      </p>
                    )}
                  </div>

                </div>

                {/* Arrow */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="hidden sm:block text-[10px] font-bold text-slate-400 group-hover:text-blue-500 transition">
                    View Checklist
                  </span>

                  <FiChevronRight className="w-4 h-4 text-slate-300 group-hover:text-blue-500 group-hover:translate-x-0.5 transition-all" />
                </div>

              </div>
            ))}

          </div>

        ) : (

          <div className="py-10 text-center">
            <FiClipboard className="w-8 h-8 text-slate-300 mx-auto mb-2" />

            <p className="text-sm font-bold text-slate-500">
              {error ? 'Unable to load checklists' : 'No checklists found'}
            </p>

            <p className="text-xs text-slate-400 mt-1">
              There are currently no compliance checklists available.
            </p>
          </div>

        )}

      </div>
    </div>
  );
}
